import { createStage } from './render/stage';
import { installErrorHandlers } from './observability/errors';
import { startWebVitals } from './observability/webVitals';
import {
  createMetricsCollector,
  type MetricsCollector,
  type MetricsReport,
} from './observability/metrics';
import { mountMetricsPanel } from './observability/metricsPanel';

declare global {
  interface Window {
    __PAPERCLIP_METRICS__?: MetricsCollector;
  }
}

async function bootstrap(): Promise<void> {
  // 1. Metrics first so it can count errors as soon as error handlers fire
  //    and so its FCP/LCP/INP/CLS adapter is wired before web-vitals runs.
  const metrics = createMetricsCollector();
  window.__PAPERCLIP_METRICS__ = metrics;

  // 2. Error capture wired to the metrics counter
  installErrorHandlers({ onError: () => metrics.recordError() });

  // 3. Web vitals with metrics adapter
  startWebVitals({ debug: true, metrics });

  // 4. Locate the host stage element
  const host = document.getElementById('stage');
  if (!host) {
    throw new Error('Stage host element #stage is missing from index.html');
  }

  // 5. Start the fps meter BEFORE creating the stage so the first rAF
  //    delta lands inside the rolling window. The stage creation awaits
  //    PixiJS init, which is itself async; onFrame in the metrics
  //    collector samples whatever is on screen.
  metrics.startFpsMeter();

  // 6. Create the stage. The stage reports the first frame via the
  //    metrics collector; the simulation loop then takes over.
  const stage = await createStage(host, {
    onFirstFrame: () => metrics.recordFirstFrame(),
  });

  // 7. Mark the scene ready and remove the loading overlay
  const app = document.getElementById('app');
  if (app) {
    app.dataset.state = 'ready';
    app.setAttribute('aria-busy', 'false');
  }
  hideLoadingOverlay();

  // 8. Mount the dev metrics panel and log a snapshot for QA capture
  mountMetricsPanel(metrics);
  const initial = metrics.getReport();
  console.info('[paperclip] M0 scaffold ready', {
    commit: __COMMIT_SHA__,
    metrics: initial,
  });

  // 9. Re-log metrics on lifecycle events so PR captures always have a
  //    fresh snapshot (LCP/INP can arrive after first paint).
  metrics.subscribe((report: MetricsReport) => {
    if (report.load.lcpMs !== null) {
      console.info('[paperclip] metrics:update', report);
    }
  });

  // Expose a teardown for future test harnesses
  window.addEventListener('beforeunload', () => {
    stage.destroy();
    metrics.stopFpsMeter();
  });
}

/**
 * Remove the loading overlay from the DOM once the scene is ready.
 *
 * The CSS rule that fades `#loading` to opacity 0 still runs (kept for
 * the visual transition), but the element is also pulled out of the DOM
 * a tick later so:
 *  - it disappears from the accessibility tree
 *  - it is removed from the tab order
 *  - it can never block input even if a future CSS regression breaks
 *    the opacity rule
 */
function hideLoadingOverlay(): void {
  const loading = document.getElementById('loading');
  if (!loading) return;
  loading.setAttribute('aria-hidden', 'true');
  loading.setAttribute('inert', '');
  // Defer removal by one frame so the CSS opacity transition has a chance
  // to run; removeChild on the next paint avoids any flash of the canvas
  // before the overlay fully fades.
  requestAnimationFrame(() => {
    if (loading.isConnected) loading.remove();
  });
}

bootstrap().catch((err: unknown) => {
  console.error('[paperclip] bootstrap failed', err);
  // If the app failed to start, the loading overlay is the only UI the
  // user can see; flip it into an error state so the message is honest.
  const loading = document.getElementById('loading');
  if (loading) {
    loading.textContent = 'Failed to start. See console for details.';
    loading.setAttribute('role', 'alert');
  }
  const app = document.getElementById('app');
  if (app) {
    app.dataset.state = 'error';
  }
});
